import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Mail, ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { apiUrl } from '../utils/config';
import logger from '../utils/logger';

const SubscriberModal = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    subscriptionType: 'both',
  });

  useEffect(() => {
    const dismissed = sessionStorage.getItem('coachmeupp_sub_dismissed');
    const subscribed = localStorage.getItem('coachmeupp_subscribed');
    if (dismissed || subscribed) return;

    const timer = setTimeout(() => setIsOpen(true), 15000);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem('coachmeupp_sub_dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(apiUrl('subscribers'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (data.success) {
        setIsSuccess(true);
        localStorage.setItem('coachmeupp_subscribed', 'true');
        toast.success(data.message);
        setTimeout(() => {
          setIsOpen(false);
        }, 3000);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
      logger.error('Subscription error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="subscriber-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors z-10"
          data-testid="subscriber-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {isSuccess ? (
          /* Success State */
          <div className="p-10 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {t('subscriber.successTitle', "You're in!")}
            </h3>
            <p className="text-gray-600">
              {t('subscriber.successMessage', "Welcome to the CoachMeUpp community. We'll be in touch soon.")}
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 px-8 pt-8 pb-6 text-white">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-medium text-blue-300 uppercase tracking-wider">
                  {t('subscriber.badge', 'Join Our Community')}
                </span>
              </div>
              <h2 className="text-2xl font-bold leading-tight mb-2" data-testid="subscriber-headline">
                {t('subscriber.headline', 'We help executives turn potential into performance.')}
              </h2>
              <p className="text-slate-300 text-base">
                {t('subscriber.subheadline', 'Join to see how.')}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('subscriber.name', 'Name')} *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Smith"
                    required
                    data-testid="subscriber-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('subscriber.email', 'Email')} *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@company.com"
                    required
                    data-testid="subscriber-email"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('subscriber.company', 'Company')}
                  </label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Acme Inc."
                    data-testid="subscriber-company"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('subscriber.role', 'Role')}
                  </label>
                  <Input
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    placeholder="VP of People"
                    data-testid="subscriber-role"
                  />
                </div>
              </div>

              {/* Subscription Type Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('subscriber.interestedIn', "I'm interested in")}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'newsletter', label: t('subscriber.typeNewsletter', 'Newsletter & Blog') },
                    { value: 'services', label: t('subscriber.typeServices', 'Service Updates') },
                    { value: 'both', label: t('subscriber.typeBoth', 'Both') },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, subscriptionType: option.value }))}
                      className={`px-3 py-2 text-sm rounded-lg border-2 font-medium transition-all ${
                        formData.subscriptionType === option.value
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid={`sub-type-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !formData.name || !formData.email}
                className="w-full bg-blue-600 hover:bg-blue-700 py-5 text-base font-semibold"
                data-testid="subscriber-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4 animate-pulse" />
                    {t('subscriber.submitting', 'Subscribing...')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {t('subscriber.cta', 'Subscribe Now')}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>

              <p className="text-xs text-gray-400 text-center">
                {t('subscriber.privacy', 'We respect your privacy. Unsubscribe at any time.')}
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriberModal;
